/**
 * ROOM WORKSPACE HISTORY (PRODUCT.md §4, R3) — the room workspace is a git repo: every saved run is a commit (which
 * agent, what, why). This is its History view inside the workspace panel (Files | History — one view):
 *   • commits, newest first — who (agent) · what (title) · when · files changed (+/−)
 *   • click a commit → its changed files + the unified diff
 *   • "Download history (git)" → the full repo as a git bundle (``git clone`` it) — yours to keep, any time
 *
 *   • a planning room's runs WAIT ON REVIEW (R3c): Approve / Reject on the commit; approved work merges into main
 *   • Publish (R3b): main → a branch of the USER'S OWN repo (a code connector) — on demand or automatically
 *
 *   <WorkspaceHistory workspaceId="ws-…" canDecide onDecide={(branch, 'approve'|'reject')} publish={…} />
 */
import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, Download, GitCommit } from 'lucide-react';
import { getWorkspaceHistory, getWorkspaceCommit, exportWorkspaceHistory } from '../../services/toolsApi';
import Spinner from '../Spinner.jsx';

const when = (iso) => String(iso || '').replace('T', ' ').slice(0, 16);

export function DiffView({ diff = '' }) {
  const lines = String(diff || '').split('\n').slice(0, 4000);
  return (
    <pre className="m-0 overflow-auto bg-gray-50 p-2 font-mono text-[11px] leading-relaxed" data-testid="ws-history-diff">
      {lines.map((l, i) => {
        const cls = l.startsWith('+++') || l.startsWith('---') ? 'text-gray-500'
          : l.startsWith('+') ? 'bg-emerald-50 text-emerald-800'
            : l.startsWith('-') ? 'bg-rose-50 text-rose-800'
              : l.startsWith('@@') ? 'text-indigo-600'
                : l.startsWith('diff --git') ? 'mt-2 font-semibold text-gray-700' : 'text-gray-700';
        return <div key={i} className={cls}>{l || ' '}</div>;
      })}
    </pre>
  );
}

const STATUS = {
  pending: ['Waiting for review', 'bg-amber-50 text-amber-800 ring-amber-200'],
  merged: ['', ''],
  rejected: ['Rejected', 'bg-gray-100 text-gray-500 ring-gray-200'],
  conflict: ['Needs a person — conflicts with newer work', 'bg-rose-50 text-rose-700 ring-rose-200'],
};

export function PublishBar({ publish }) {
  const t = publish?.target || {};
  const [connector, setConnector] = useState(t.connectorId || '');
  const [branch, setBranch] = useState(t.branch || '');
  const [auto, setAuto] = useState(!!t.auto);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  useEffect(() => { setConnector(t.connectorId || ''); setBranch(t.branch || ''); setAuto(!!t.auto); },
    [t.connectorId, t.branch, t.auto]);
  if (!publish) return null;
  const run = async (what, fn) => {
    setBusy(what); setMsg('');
    try { await fn(); setMsg(what === 'publish' ? 'Publishing — it shows here when it lands.' : 'Saved.'); }
    catch (e) { setMsg(e?.message || "Couldn't do that just now."); }
    setBusy('');
  };
  const last = publish.last;
  return (
    <div className="border-b border-gray-100 px-3 py-2 text-[11px]" data-testid="ws-publish">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="font-medium text-gray-700">Publish to your repo</span>
        <select aria-label="Code connector" value={connector} disabled={!publish.canSet} data-testid="ws-publish-connector"
          onChange={(e) => setConnector(e.target.value)} className="rounded border border-gray-300 px-1 py-0.5 text-[11px]">
          <option value="">Not published</option>
          {(publish.connectors || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <input aria-label="Branch" value={branch} disabled={!publish.canSet} onChange={(e) => setBranch(e.target.value)}
          data-testid="ws-publish-branch" className="w-36 rounded border border-gray-300 px-1.5 py-0.5 font-mono text-[11px]" />
        <label className="inline-flex items-center gap-1 text-gray-600">
          <input type="checkbox" checked={auto} disabled={!publish.canSet} onChange={(e) => setAuto(e.target.checked)}
            data-testid="ws-publish-auto" /> on every merge
        </label>
        {publish.canSet ? (
          <button type="button" data-testid="ws-publish-save" disabled={!!busy}
            onClick={() => run('save', () => publish.onSave({ connectorId: connector, branch, auto }))}
            className="rounded-md px-2 py-0.5 font-medium text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50">Save</button>
        ) : null}
        {publish.canSet && t.connectorId ? (
          <button type="button" data-testid="ws-publish-now" disabled={!!busy}
            onClick={() => run('publish', () => publish.onPublish())}
            className="rounded-md bg-indigo-600 px-2 py-0.5 font-medium text-white hover:bg-indigo-700">Publish now</button>
        ) : null}
      </div>
      <div className="mt-0.5 text-gray-500">
        Pushes the approved history to a branch of your repo — never your main, never forced. Your code stays yours.
        {last ? <span className={`ml-1 ${last.ok ? 'text-emerald-700' : 'text-rose-600'}`} data-testid="ws-publish-last">
          Last: {last.ok ? `published to ${last.pushed || last.branch}` : last.reason} · {when(last.at)}</span> : null}
        {msg ? <span className="ml-1 text-gray-700">{msg}</span> : null}
      </div>
    </div>
  );
}

export default function WorkspaceHistory({ workspaceId, canDecide = false, onDecide = null, publish = null }) {
  const [commits, setCommits] = useState(null);
  const [next, setNext] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);       // {commit, diff}
  const [busy, setBusy] = useState(false);
  const [lastPublish, setLastPublish] = useState(null);
  const [deciding, setDeciding] = useState('');
  const decide = async (branch, action) => {
    if (typeof onDecide !== 'function') return;
    setDeciding(branch); setError('');
    try {
      await onDecide(branch, action);
      setCommits((cs) => (cs || []).map((c) => (c.branch === branch
        ? { ...c, status: action === 'approve' ? 'pending' : 'rejected', decidedLocal: action } : c)));
    } catch (e) { setError(e?.message || "Couldn't record that"); }
    setDeciding('');
  };
  const load = useCallback(async (before) => {
    setBusy(true); setError('');
    try {
      const r = await getWorkspaceHistory(workspaceId, before ? { before } : {});
      setCommits((cur) => (before ? [...(cur || []), ...(r?.commits || [])] : (r?.commits || [])));
      setNext(r?.nextBefore || null);
      if (!before) setLastPublish(r?.lastPublish || null);
    } catch (e) { setError(e?.message || "Couldn't load the history"); }
    setBusy(false);
  }, [workspaceId]);
  useEffect(() => { if (workspaceId) load(null); }, [workspaceId, load]);
  const openCommit = async (sha) => {
    setBusy(true); setError('');
    try { setOpen(await getWorkspaceCommit(workspaceId, sha)); } catch (e) { setError(e?.message || "Couldn't open it"); }
    setBusy(false);
  };
  const exportAll = async () => {
    try { const r = await exportWorkspaceHistory(workspaceId); if (r?.url) window.open(r.url, '_blank', 'noopener'); }
    catch (e) { setError(e?.message || 'No history to export yet'); }
  };
  if (!workspaceId) return <div className="p-6 text-center text-xs text-gray-500">No shared room workspace yet.</div>;
  if (commits === null && busy) return <Spinner variant="panel" label="Loading history…" testId="ws-history-loading" />;
  if (open) {
    const c = open.commit || {};
    return (
      <div className="flex min-h-0 flex-1 flex-col" data-testid="ws-history-commit">
        <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2 text-xs">
          <button type="button" onClick={() => setOpen(null)} className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-gray-600 hover:bg-gray-100">
            <ChevronLeft className="h-3.5 w-3.5" /> History
          </button>
          <span className="truncate font-medium text-gray-800">{c.title}</span>
          <span className="ml-auto shrink-0 font-mono text-[10px] text-gray-400">{String(c.sha || '').slice(0, 10)}</span>
        </div>
        <ul className="border-b border-gray-100 px-3 py-1.5 text-[11px]">
          {(c.files || []).map((f) => (
            <li key={f.path} className="flex items-center gap-2">
              <span className="w-3 font-mono text-gray-400">{f.status}</span>
              <span className="min-w-0 flex-1 truncate font-mono text-gray-700">{f.path}</span>
              {f.binary ? <span className="text-gray-400">binary</span> : (
                <span className="font-mono"><span className="text-emerald-600">+{f.additions}</span> <span className="text-rose-600">−{f.deletions}</span></span>)}
            </li>
          ))}
        </ul>
        <div className="min-h-0 flex-1 overflow-auto"><DiffView diff={open.diff} /></div>
      </div>
    );
  }
  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="ws-history">
      <div className="flex items-center justify-between border-b border-gray-100 px-3 py-1.5 text-[11px] text-gray-500">
        <span>Every saved run is a commit — who did what, and why.</span>
        <button type="button" onClick={exportAll} data-testid="ws-history-export"
          className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
          <Download className="h-3.5 w-3.5" /> Download history (git)
        </button>
      </div>
      {publish ? <PublishBar publish={{ ...publish, last: lastPublish }} /> : null}
      {error ? <div className="px-3 py-2 text-xs text-rose-600">{error}</div> : null}
      {commits && !commits.length ? (
        <div className="p-6 text-center text-xs text-gray-500">No history yet — it starts with the next saved run.</div>
      ) : (
        <ul className="min-h-0 flex-1 divide-y divide-gray-100 overflow-auto">
          {(commits || []).map((c) => {
            const add = (c.files || []).reduce((n, f) => n + (f.additions || 0), 0);
            const del = (c.files || []).reduce((n, f) => n + (f.deletions || 0), 0);
            return (
              <li key={c.sha}>
                <button type="button" onClick={() => openCommit(c.sha)} data-testid="ws-history-row"
                  className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-gray-50">
                  <GitCommit className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-medium text-gray-800">{c.title}</span>
                    <span className="block text-[11px] text-gray-500">
                      {when(c.at)} · {c.fileCount || 0} file{c.fileCount === 1 ? '' : 's'}
                      {c.task ? ` · ${c.task}` : ''}
                    </span>
                  </span>
                  <span className="shrink-0 font-mono text-[11px]"><span className="text-emerald-600">+{add}</span> <span className="text-rose-600">−{del}</span></span>
                </button>
                {STATUS[c.status] && STATUS[c.status][0] ? (
                  <div className="flex items-center gap-1.5 px-3 pb-2 pl-8 text-[11px]" data-testid="ws-history-status">
                    <span className={`rounded px-1.5 py-px ring-1 ${STATUS[c.status][1]}`}>
                      {c.decidedLocal === 'approve' ? 'Approved — merging…' : STATUS[c.status][0]}
                    </span>
                    {canDecide && (c.status === 'pending' || c.status === 'conflict') && !c.decidedLocal && c.branch ? (<>
                      {c.status === 'pending' ? (
                        <button type="button" data-testid="ws-history-approve" disabled={deciding === c.branch}
                          onClick={() => decide(c.branch, 'approve')}
                          className="rounded-md bg-indigo-600 px-2 py-0.5 font-medium text-white hover:bg-indigo-700">Approve</button>
                      ) : null}
                      <button type="button" data-testid="ws-history-reject" disabled={deciding === c.branch}
                        onClick={() => decide(c.branch, 'reject')}
                        className="rounded-md px-2 py-0.5 font-medium text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50">Reject</button>
                    </>) : null}
                  </div>
                ) : null}
              </li>
            );
          })}
          {next ? (
            <li className="p-2 text-center">
              <button type="button" onClick={() => load(next)} disabled={busy} className="text-xs text-indigo-600 hover:underline">Older…</button>
            </li>
          ) : null}
        </ul>
      )}
    </div>
  );
}
